// Live's "Save to project" (#267 part 2c, `editor-depth/10`, SPEC.md B9): where the video is kept
// in the brand's library, then Save renders it there on the server. The brand's folders are
// live's "Projects", shown as its tree (spec A3b); New Project makes one.
import { Button, Group, Modal, ScrollArea, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Folder, FolderPlus, LayoutGrid, Search } from "lucide-react";
import { useMemo, useState } from "react";

import {
  getListFoldersQueryKey,
  useCreateFolder,
  useListFolders,
} from "../../api/generated/endpoints/teams/teams";
import { folderInNameMax } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { folderTree, searchedIds } from "../../platform/folders/folder-tree";
import { FolderTreeList } from "../../platform/folders/FolderTreeList";
import classes from "./editor.module.css";

export interface SaveToProjectProps {
  teamId: string;
  brandId: string;
  opened: boolean;
  onClose: () => void;
  /** Save: the chosen folder, or null to keep the video unfiled ("All assets & files"). */
  onSave: (folderId: string | null) => void;
}

/**
 * Live's dialog: a search ("Search", run on Enter or its button), "All assets & files" (the
 * video kept unfiled), "Projects" with the brand's folders under it, a name and "New Project",
 * then Save and Cancel. The folder chosen last is chosen again next time, as live's keeps it.
 */
export function SaveToProject(props: SaveToProjectProps) {
  const { teamId, brandId, opened, onClose, onSave } = props;
  const queryClient = useQueryClient();
  const folders = useListFolders(teamId, brandId, { query: { enabled: opened } });
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [name, setName] = useState("");
  const create = useCreateFolder({
    mutation: {
      onSuccess: async (made) => {
        setName("");
        setChosen(made.id);
        await queryClient.invalidateQueries({ queryKey: getListFoldersQueryKey(teamId, brandId) });
      },
    },
  });
  const tree = useMemo(() => folderTree(folders.data ?? []), [folders.data]);
  const hits = searchedIds(tree, search);
  const makeFolder = () => {
    if (name.trim() === "" || create.isPending) return;
    create.mutate({ teamId, brandId, data: { name: name.trim() } });
  };
  const all = (
    <UnstyledButton
      className={classes.saveRow}
      aria-pressed={chosen === null}
      onClick={() => setChosen(null)}
    >
      <LayoutGrid size={16} aria-hidden />
      <span>All assets & files</span>
    </UnstyledButton>
  );
  return (
    <Modal opened={opened} onClose={onClose} title="Save to project" size={360} centered>
      <Group gap={8} wrap="nowrap" className={classes.saveSearch}>
        <TextInput
          aria-label="Search"
          placeholder="Search"
          leftSection={<Search size={16} aria-hidden />}
          value={typed}
          onChange={(event) => setTyped(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") setSearch(typed);
          }}
          className={classes.saveSearchInput}
        />
        <Button variant="subtle" size="sm" onClick={() => setSearch(typed)}>
          Search
        </Button>
      </Group>
      <ScrollArea.Autosize mah={240} className={classes.saveTree}>
        {all}
        <Text className={classes.saveHeading}>
          <Folder size={16} aria-hidden /> Projects
        </Text>
        {folders.isError ? <ErrorAlert error={folders.error} /> : null}
        {folders.isSuccess ? (
          <FolderTreeList
            key={search}
            tree={tree}
            selectedId={chosen}
            onSelect={(folder) => setChosen(folder.id)}
            shownIds={hits}
            label="Projects"
          />
        ) : null}
        {folders.isSuccess && (hits === undefined ? tree.children(null).length === 0 : hits.size === 0) ? (
          <Text size="sm" c="var(--cai-color-text-muted)" className={classes.saveEmpty}>
            {search.trim() === "" ? "No projects yet" : "No projects found"}
          </Text>
        ) : null}
      </ScrollArea.Autosize>
      <Group gap={8} wrap="nowrap" className={classes.saveNew}>
        <TextInput
          aria-label="Project name"
          placeholder="Enter project name"
          value={name}
          maxLength={folderInNameMax}
          onChange={(event) => setName(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") makeFolder();
          }}
          className={classes.saveSearchInput}
        />
        <Button
          variant="subtle"
          size="sm"
          leftSection={<FolderPlus size={16} aria-hidden />}
          disabled={name.trim() === ""}
          loading={create.isPending}
          onClick={makeFolder}
        >
          New Project
        </Button>
      </Group>
      {create.isError ? <ErrorAlert error={create.error} /> : null}
      <Group gap={8} mt="md">
        <Button className={classes.downloadButton} onClick={() => onSave(chosen)}>
          Save
        </Button>
        <Button variant="subtle" color="gray" onClick={onClose}>
          Cancel
        </Button>
      </Group>
    </Modal>
  );
}
